import singleUrl from './figures.single.json?url';
import pairUrl from './figures.pair.json?url';
import team4Url from './figures.team4.json?url';
import team6Url from './figures.team6.json?url';
import type { Catalogue, CatalogueKey, Exercise, FigureVariant, StarterType } from '../types';
import { STARTER_TYPES } from '../domain/constants';

const URLS: Readonly<Record<CatalogueKey, string>> = {
  single: singleUrl,
  pair: pairUrl,
  team4: team4Url,
  team6: team6Url,
};

const loaded = new Map<CatalogueKey, Catalogue>();
const inFlight = new Map<CatalogueKey, Promise<Catalogue>>();

// A tiny external store (mirrors WorkspaceStore's own subscribe/version
// pattern, see src/ui/hooks/useWorkspace.ts) so `useCatalogue()`/
// `useCatalogues()` can re-render as each catalogue finishes loading,
// without every consumer re-implementing its own fetch-and-cache dance.
type Listener = () => void;
const listeners = new Set<Listener>();
let version = 0;

function notify(): void {
  version++;
  listeners.forEach((l) => l());
}

export function subscribeCatalogues(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function catalogueVersion(): number {
  return version;
}

/** Whatever is already cached for `key`, without triggering a fetch. */
export function getLoadedCatalogue(key: CatalogueKey): Catalogue | undefined {
  return loaded.get(key);
}

/**
 * Fetch a catalogue once and cache it. The four files are kept out of the
 * main bundle and requested per discipline — a solo user never downloads
 * the pair/team4/team6 catalogues at all.
 */
export function loadCatalogue(key: CatalogueKey): Promise<Catalogue> {
  const cached = loaded.get(key);
  if (cached) return Promise.resolve(cached);

  const pending = inFlight.get(key);
  if (pending) return pending;

  const req = fetch(URLS[key])
    .then((res) => {
      if (!res.ok) throw new Error(`Übungskatalog ${key} nicht ladbar (HTTP ${res.status}).`);
      return res.json() as Promise<Catalogue>;
    })
    .then((cat) => {
      loaded.set(key, cat);
      inFlight.delete(key);
      notify();
      return cat;
    })
    .catch((err: unknown) => {
      inFlight.delete(key);
      throw err;
    });

  inFlight.set(key, req);
  return req;
}

export function catalogueKeyFor(type: StarterType): CatalogueKey {
  return STARTER_TYPES[type].catalogue;
}

const indexes = new Map<CatalogueKey, ReadonlyMap<string, FigureVariant>>();

/** Figure number -> variant, for resolving a programme entry back to source. */
export function variantIndex(cat: Catalogue): ReadonlyMap<string, FigureVariant> {
  const existing = indexes.get(cat.key);
  if (existing) return existing;
  const map = new Map<string, FigureVariant>();
  for (const fig of cat.figures) {
    for (const v of fig.variants) map.set(v.id, v);
  }
  indexes.set(cat.key, map);
  return map;
}

/**
 * Resolves every exercise in a programme against `cat`, in order —
 * `undefined` for a figure number the catalogue doesn't (or no longer)
 * carry, e.g. a stale share link or an old file after a catalogue update.
 * Callers render/skip that case explicitly rather than crashing.
 */
export function resolveProgram(cat: Catalogue, program: readonly Exercise[]): ReadonlyArray<FigureVariant | undefined> {
  const index = variantIndex(cat);
  return program.map((ex) => index.get(ex.nr));
}

/** Same as `resolveProgram()`, but looks the right catalogue up by starter
 *  type from an already-loaded set (see `useCatalogues()`) — every field on
 *  `Exercise` other than the number is only ever available once the
 *  matching catalogue has loaded, so a still-loading or failed fetch
 *  resolves every figure to `undefined` rather than throwing. */
export function resolveProgramForType(
  catalogues: ReadonlyMap<CatalogueKey, Catalogue>,
  type: StarterType,
  program: readonly Exercise[],
): ReadonlyArray<FigureVariant | undefined> {
  const cat = catalogues.get(catalogueKeyFor(type));
  return cat ? resolveProgram(cat, program) : program.map(() => undefined);
}

/** The `Exercise` for adding a catalogue figure to a programme — the single
 *  source of truth for that mapping, used by the figure picker, the WeBo
 *  importer and the share-link importer. Just the id: see `Exercise` in
 *  types.ts for why nothing else is copied. */
export function exerciseFromVariant(v: FigureVariant): Exercise {
  return { nr: v.id };
}
